import { parseAsBoolean, parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import type { FilterField } from './filters/filterUtils';
import {
  buildFilterParsers,
  buildGraphQLFilter,
  clearPayload,
  hasActiveFilters,
  normalizeFilters,
} from './filters/filterUtils';

type QueryParamParser<Type> = {
  parse: (value: string | null) => Type;
  serialize: (value: Type) => string;
  defaultValue?: Type;
};

export interface NotificationFilters {
  machines: string[];
  alarms: string[];
}

const fields: Array<FilterField<keyof NotificationFilters>> = [
  { key: 'machines', type: 'multi', graphKey: 'machineIds' },
  { key: 'alarms', type: 'multi', graphKey: 'alarmIds' },
];

const filterParsers = buildFilterParsers<keyof NotificationFilters>(fields) as {
  [K in keyof NotificationFilters]: QueryParamParser<NotificationFilters[K]>;
};

// Combined parsers including the 'id' param for side panel
const allParsers = {
  ...filterParsers,
  id: parseAsString.withDefault(''),
};

// Separate parser for allUsers toggle (not part of the generic filter system)
const allUsersParsers = {
  showAll: parseAsBoolean.withDefault(false),
};

/**
 * Hook to manage notification filters via URL query params.
 * This is the single source of truth for notification filter state.
 * No prop drilling needed - use this hook anywhere you need notification filters.
 */
export function useNotificationFilters() {
  const [state, setUrlState] = useQueryStates(allParsers);
  const [showAllState, setShowAllState] = useQueryStates(allUsersParsers);

  /** When true, notifications from all users are shown. When false, only the current user's. */
  const showAllUsers = showAllState.showAll;

  const normalizedFilters = useMemo(
    () =>
      normalizeFilters<NotificationFilters, keyof NotificationFilters>(
        state,
        fields
      ),
    [state]
  );

  const setMachines = useCallback(
    (values: string[]) => {
      setUrlState({ machines: values.length > 0 ? values : null });
    },
    [setUrlState]
  );

  const setAlarms = useCallback(
    (values: string[]) => {
      setUrlState({ alarms: values.length > 0 ? values : null });
    },
    [setUrlState]
  );

  // Manage selected notification ID (for side panel)
  const selectedId = state.id || null;

  const setSelectedId = useCallback(
    (id: string | null) => {
      setUrlState({ id: id || null });
    },
    [setUrlState]
  );

  const setShowAllUsers = useCallback(
    (value: boolean) => {
      setShowAllState({ showAll: value || null });
    },
    [setShowAllState]
  );

  const setFilters = useCallback(
    (newFilters: Partial<NotificationFilters>) => {
      const currentMachines = Array.isArray(state.machines)
        ? state.machines
        : [];
      const currentAlarms = Array.isArray(state.alarms) ? state.alarms : [];

      setUrlState({
        machines:
          newFilters.machines !== undefined
            ? newFilters.machines.length > 0
              ? newFilters.machines
              : null
            : currentMachines.length > 0
              ? currentMachines
              : null,
        alarms:
          newFilters.alarms !== undefined
            ? newFilters.alarms.length > 0
              ? newFilters.alarms
              : null
            : currentAlarms.length > 0
              ? currentAlarms
              : null,
      });
    },
    [setUrlState, state]
  );

  const clearFilters = useCallback(() => {
    setUrlState(clearPayload(fields));
  }, [setUrlState]);

  const graphQLFilter = useMemo(() => {
    const base = buildGraphQLFilter(state, fields);
    // Merge allUsers into the GraphQL filter when showing all users' notifications
    if (showAllUsers) {
      return { ...base, allUsers: true };
    }
    return base;
  }, [state, showAllUsers]);

  return {
    filters: normalizedFilters,
    showAllUsers,
    setMachines,
    setAlarms,
    setShowAllUsers,
    setFilters,
    clearFilters,
    hasActiveFilters: hasActiveFilters(state, fields),
    graphQLFilter,
    // Side panel state
    selectedId,
    setSelectedId,
  };
}
